import {
  ArrowLeft,
  Check,
  Loader2,
  MessageSquarePlus,
  MoreHorizontal,
  Pin,
  Search,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import {
  useChatConversations,
  useDeleteChatConversation,
  useUpdateChatConversation,
} from '../../hooks/useChatConversations';
import type { ConversationSummary } from '../../lib/chatApi';
import type { TranslateParams } from '../../lib/i18n';
import { SidebarHeaderControls } from '../layout/SidebarHeaderControls';
import { Popover } from '../tasks/linear/Popover';
import { useConfirm } from '../ui/ConfirmProvider';

type TranslateFn = (key: string, params?: TranslateParams) => string;

/** Format an ISO timestamp as a localized relative label ("acum 2 ore", "ieri"). */
function relativeTime(iso: string, t: TranslateFn): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return t('chat.relative.now');
  if (min < 60)
    return t(min === 1 ? 'chat.relative.minuteOne' : 'chat.relative.minuteMany', { count: min });
  const hours = Math.floor(min / 60);
  if (hours < 24)
    return t(hours === 1 ? 'chat.relative.hourOne' : 'chat.relative.hourMany', { count: hours });
  const days = Math.floor(hours / 24);
  if (days === 1) return t('chat.relative.yesterday');
  if (days < 7) return t('chat.relative.daysMany', { count: days });
  return new Date(iso).toLocaleDateString('ro-RO', { day: 'numeric', month: 'long' });
}

/**
 * Replaces the app sidebar while on /chat*: the AI assistant's conversation
 * history, the same shell team chat gets. Living in the sidebar rather than
 * inside the page means one drawer on mobile instead of two stacked ones, and
 * the full sidebar width on desktop instead of a column carved out of the page.
 */
export function ChatHistorySidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const confirm = useConfirm();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Search runs server-side across titles AND turn content, so wait for a
  // pause in typing rather than firing a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(handle);
  }, [search]);

  const { data, isPending } = useChatConversations(debouncedSearch);
  const updateConversation = useUpdateChatConversation();
  const deleteConversation = useDeleteChatConversation();

  const conversations = data ?? [];
  // The URL owns which thread is open, exactly like /team-chat — that's what
  // lets this list live outside the page.
  const activeId = pathname.startsWith('/chat/') ? pathname.slice('/chat/'.length) : null;

  const go = (to: string) => {
    navigate(to);
    onNavigate?.();
  };

  const commitRename = (id: string) => {
    const title = renameValue.trim();
    setRenamingId(null);
    if (title) updateConversation.mutate({ id, changes: { title } });
  };

  const remove = async (conversation: ConversationSummary) => {
    const title = conversation.title ?? t('chat.untitled');
    if (!(await confirm({ message: t('chat.deleteConfirm', { title }), danger: true }))) return;
    deleteConversation.mutate(conversation.id, {
      // Deleting the thread you're reading would leave the page showing turns
      // that no longer exist; fall back to a fresh chat.
      onSuccess: () => {
        if (conversation.id === activeId) navigate('/chat');
      },
    });
  };

  return (
    <nav className="flex h-full flex-col overflow-hidden pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]">
      <div className="flex items-center justify-between px-4 py-4 pt-[calc(1rem+env(safe-area-inset-top))]">
        <Link
          to="/dashboard"
          onClick={onNavigate}
          className="flex items-center gap-2 text-sm font-bold text-sidebar-foreground hover:text-foreground"
        >
          <ArrowLeft size={16} />
          {t('chat.backToApp')}
        </Link>
        <SidebarHeaderControls />
      </div>

      <div className="space-y-3 px-3">
        <button
          type="button"
          onClick={() => go('/chat')}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          <MessageSquarePlus size={16} className="shrink-0" />
          <span className="truncate">{t('chat.newConversation')}</span>
        </button>
        <div className="relative">
          <Search
            size={14}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('chat.searchConversations')}
            className="w-full rounded-md border border-sidebar-border bg-background py-1.5 pl-8 pr-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
          />
        </div>
      </div>

      <div className="mt-3 flex-1 space-y-1 overflow-y-auto px-3 pb-6">
        {isPending && conversations.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
            <Loader2 size={14} className="mr-2 animate-spin" /> {t('chat.uploading')}
          </div>
        ) : conversations.length === 0 ? (
          <p className="px-2 py-8 text-center text-xs text-muted-foreground">
            {debouncedSearch ? t('chat.noResults') : t('chat.noConversations')}
          </p>
        ) : (
          conversations.map((conversation) =>
            renamingId === conversation.id ? (
              <div
                key={conversation.id}
                className="flex items-center gap-1 rounded-md border border-primary bg-background p-1"
              >
                <input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename(conversation.id);
                    if (e.key === 'Escape') setRenamingId(null);
                  }}
                  className="min-w-0 flex-1 bg-transparent px-1 text-sm text-foreground outline-none"
                />
                <button
                  type="button"
                  onClick={() => commitRename(conversation.id)}
                  aria-label={t('chat.save')}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Check size={14} />
                </button>
              </div>
            ) : (
              <div
                key={conversation.id}
                onClick={() => go(`/chat/${conversation.id}`)}
                className={`group flex cursor-pointer flex-col rounded-md border px-2.5 py-2 ${
                  conversation.id === activeId
                    ? 'border-primary bg-sidebar-accent'
                    : 'border-transparent hover:bg-sidebar-accent'
                }`}
              >
                <div className="flex items-center gap-1">
                  {conversation.pinned && (
                    <Pin size={11} className="shrink-0 text-primary" fill="currentColor" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                    {conversation.title || t('chat.untitled')}
                  </span>
                  {/* One ⋯ rather than three icons: on touch every <button> is
                      inflated to a 44px hit area (index.css), so three of them
                      ate half the row and left the title as an ellipsis. */}
                  <Popover
                    align="end"
                    width={180}
                    trigger={({ open, toggle, ref }) => (
                      <button
                        type="button"
                        ref={ref}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle();
                        }}
                        aria-haspopup="menu"
                        aria-expanded={open}
                        aria-label={t('chat.actions')}
                        className={`-mr-1 flex shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground ${
                          open
                            ? ''
                            : 'md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100 touch:!opacity-100'
                        }`}
                      >
                        <MoreHorizontal size={16} />
                      </button>
                    )}
                  >
                    {(close) => (
                      <div className="py-1">
                        <button
                          type="button"
                          onClick={() => {
                            updateConversation.mutate({
                              id: conversation.id,
                              changes: { pinned: !conversation.pinned },
                            });
                            close();
                          }}
                          className="block w-full px-3 py-1.5 text-left text-[13px] text-foreground hover:bg-accent"
                        >
                          {conversation.pinned ? t('chat.unpin') : t('chat.pin')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRenamingId(conversation.id);
                            setRenameValue(conversation.title ?? '');
                            close();
                          }}
                          className="block w-full px-3 py-1.5 text-left text-[13px] text-foreground hover:bg-accent"
                        >
                          {t('chat.rename')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            close();
                            void remove(conversation);
                          }}
                          className="block w-full px-3 py-1.5 text-left text-[13px] text-destructive hover:bg-accent"
                        >
                          {t('chat.delete')}
                        </button>
                      </div>
                    )}
                  </Popover>
                </div>
                {conversation.preview && (
                  <span className="mt-0.5 truncate text-xs text-muted-foreground">
                    {conversation.preview}
                  </span>
                )}
                <span className="mt-0.5 text-[11px] text-muted-foreground">
                  {relativeTime(conversation.updatedAt, t)}
                </span>
              </div>
            ),
          )
        )}
      </div>
    </nav>
  );
}
